import React from 'react';
import styled, { keyframes } from 'styled-components';
import colors from 'theme/colors';
import indexPriority from 'theme/z-index';
import { useCollapse } from 'context/general/Collapse';
import { useLocation, useSearchParams } from 'react-router-dom';
import MultipleEventsList from './MultipleEventsList';
import MultipleEstablishmentsList from './MultipleEstablishmentsList';
import paths from 'routes/paths';

const THUMB_SIZE = 30;
const OVERLAP = 10;
const MAX_VISIBLE = 3;
const BADGE_SIZE = 22;
const TAIL_HEIGHT = 7;

function ThumbItem({ el, index }) {
    const src = el.thumb || el?.profileImage?.thumb || '/assets/images/placeholder-events.png';
    return (
        <Thumbnail $index={index}>
            <ThumbImage
                src={src}
                alt={el.name}
                onError={(e) => { e.target.onerror = null; e.target.src = '/assets/images/placeholder-events.png'; }}
            />
        </Thumbnail>
    );
}

export default function MultipleMarkers({ data, specialData, key, onGroupClick }) {
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { collapse, setCollapse } = useCollapse();

    const isEventTab = location.pathname.includes(paths.events.home);
    const isEstablishment = location.pathname.includes(paths.establishments.home);

    const visibleItems = data.slice(0, MAX_VISIBLE);
    const remainingCount = data.length - MAX_VISIBLE;

    const stackWidth = THUMB_SIZE + (visibleItems.length - 1) * (THUMB_SIZE - OVERLAP);

    async function showMultiples() {
        if (onGroupClick) {
            onGroupClick(data);
            return;
        }

        const payload = {
            lat: data[0].address?.lat,
            long: data[0].address?.long,
        };

        setSearchParams(payload);

        if (isEventTab) {
            setCollapse({
                ...collapse,
                withContent: true,
                height: 'max',
                arrowPosition: false,
                componentToRender: () => data && <MultipleEventsList data={data} />,
            });
        }

        if (isEstablishment) {
            setCollapse({
                ...collapse,
                withContent: true,
                height: 'max',
                arrowPosition: false,
                componentToRender: () => data && <MultipleEstablishmentsList data={data} />,
            });
        }
    }

    return (
        <ClusterWrapper
            id="map_marker_multiple"
            $width={stackWidth}
            onClick={() => showMultiples(data)}
        >
            <StackContainer>
                {visibleItems.map((el, i) => (
                    <ThumbItem key={el.slug} el={el} index={i} />
                ))}

                {remainingCount > 0 && (
                    <CountBadge $offset={visibleItems.length}>
                        +{remainingCount}
                    </CountBadge>
                )}
            </StackContainer>
            <ClusterTail />
        </ClusterWrapper>
    );
}

const popIn = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.6);
  }
  60% {
    transform: scale(1.06);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

const ClusterWrapper = styled.div`
    position: absolute;
    left: ${({ $width }) => -($width / 2)}px;
    top: ${-(THUMB_SIZE + TAIL_HEIGHT + 4)}px;
    cursor: pointer;
    z-index: ${indexPriority.medium};
    filter: drop-shadow(0 2px 8px rgba(26, 26, 26, 0.22));
    animation: ${popIn} 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) both;
    transition: transform 0.18s cubic-bezier(0.34, 1.56, 0.64, 1),
                filter 0.18s ease;

    &:hover {
        transform: scale(1.08) translateY(-2px);
        filter: drop-shadow(0 6px 14px rgba(26, 26, 26, 0.28));
        z-index: ${indexPriority.high};
    }

    &:active {
        transform: scale(1.02);
    }
`;

const StackContainer = styled.div`
    display: flex;
    align-items: center;
    position: relative;
    height: ${THUMB_SIZE + 4}px;
    padding: 2px;
    background: ${colors.neutralLight};
    border-radius: ${(THUMB_SIZE + 4) / 2}px;
    border: 2px solid ${colors.occasionalPurple}30;
`;

const Thumbnail = styled.div`
    position: relative;
    width: ${THUMB_SIZE}px;
    height: ${THUMB_SIZE}px;
    border-radius: 50%;
    overflow: hidden;
    border: 2px solid ${colors.neutralLight};
    background: ${colors.neutral};
    flex-shrink: 0;
    margin-left: ${({ $index }) => $index > 0 ? `-${OVERLAP}px` : '0'};
    z-index: ${({ $index }) => MAX_VISIBLE - $index};
`;

const ThumbImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const ThumbInitial = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 700;
    color: ${colors.occasionalPurple};
    background: ${colors.neutral};
`;

const CountBadge = styled.div`
    position: relative;
    width: auto;
    min-width: ${BADGE_SIZE}px;
    height: ${BADGE_SIZE}px;
    padding: 0 5px;
    margin-left: -${OVERLAP - 2}px;
    border-radius: ${BADGE_SIZE / 2}px;
    background: ${colors.occasionalPurple};
    color: ${colors.neutralLight};
    font-size: 11px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: ${MAX_VISIBLE + 1};
    flex-shrink: 0;
`;

const ClusterTail = styled.div`
    width: 0;
    height: 0;
    margin: 0 auto;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: ${TAIL_HEIGHT}px solid ${colors.neutralLight};
`;
